import React, { useState } from 'react';
import { motion } from 'framer-motion';

import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiMail, FiCheck, FiLoader, FiAlertTriangle } = FiIcons;

const WaitlistForm = ({ className = '', buttonText = 'Join Waitlist Now', variant = 'default' }) => {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState('idle'); // idle, loading, success, error
  const [message, setMessage] = useState('');

  const isInverted = variant === 'inverted';

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!email || !email.includes('@')) {
      setStatus('error');
      setMessage('Please enter a valid email address.');
      return;
    }

    setStatus('loading');
    setMessage('');

    try {
      // Sending data to n8n Webhook
      const response = await fetch("https://n8n.firmsedge.com/webhook/ninjacopia-waitlist", {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          email: email,
          source: 'website_waitlist',
          timestamp: new Date().toISOString()
        })
      });

      if (response.ok) {
        setStatus('success');
        setMessage("You're on the list! Check your email for confirmation.");
        setEmail('');

        // Reset after 5 seconds
        setTimeout(() => {
          setStatus('idle');
          setMessage('');
        }, 5000);
      } else {
        const data = await response.json();
        if (data.errors) {
          throw new Error(data.errors.map(error => error.message).join(", "));
        } else {
          throw new Error('Oops! There was a problem joining the waitlist.');
        }
      }

    } catch (error) {
      console.error('Waitlist error:', error);
      setStatus('error');
      setMessage(error.message || 'Something went wrong. Please try again.');
    }
  };

  const buttonStyles = isInverted
    ? "bg-white hover:bg-blue-50 text-blue-600 disabled:bg-blue-200/50 disabled:text-blue-100"
    : "bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white";

  const privacyTextStyles = isInverted
    ? "text-blue-100"
    : "text-gray-500 dark:text-gray-400";

  // Success/Error message colors
  const successColor = isInverted ? "text-white font-medium bg-green-500/20 px-3 py-1 rounded inline-block" : "text-green-600 dark:text-green-400";
  const errorColor = isInverted ? "text-red-200 bg-red-900/20 px-3 py-1 rounded inline-block" : "text-red-600 dark:text-red-400";
  const errorIconColor = isInverted ? "text-red-200" : "text-gray-500 dark:text-gray-400";
  const supportLinkColor = isInverted ? "text-white underline hover:text-blue-100" : "text-blue-600 dark:text-blue-400 hover:underline";

  return (
    <div className={className}>
      <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
        <div className="flex-1 relative">
          <SafeIcon
            icon={FiMail}
            className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5"
          />
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Enter your email address"
            className="w-full pl-10 pr-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
            disabled={status === 'loading'}
          />
        </div>

        <button
          type="submit"
          disabled={status === 'loading' || status === 'success'}
          className={`px-6 py-3 font-semibold rounded-lg transition-all duration-200 flex items-center justify-center min-w-[140px] ${buttonStyles}`}
        >
          {status === 'loading' && (
            <SafeIcon icon={FiLoader} className="w-5 h-5 animate-spin mr-2" />
          )}
          {status === 'success' && (
            <SafeIcon icon={FiCheck} className="w-5 h-5 mr-2" />
          )}
          {status === 'success' ? 'Added!' : status === 'loading' ? 'Adding...' : buttonText}
        </button>
      </form>

      {message && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className={`mt-3 text-center text-sm ${status === 'success' ? successColor : errorColor}`}
        >
          {message}
        </motion.div>
      )}

      {status === 'error' && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mt-3 text-center"
        >
          <div className={`flex items-center justify-center text-sm ${errorIconColor}`}>
            <SafeIcon icon={FiAlertTriangle} className="w-4 h-4 mr-2" />
            Having trouble? Email us directly at{' '}
            <a href="mailto:support@ninjacopia.com" className={`ml-1 ${supportLinkColor}`}>
              support@ninjacopia.com
            </a>
          </div>
        </motion.div>
      )}

      <p className={`text-xs text-center mt-2 ${privacyTextStyles}`}>
        We respect your privacy. No spam. Unsubscribe anytime.
      </p>
    </div>
  );
};

export default WaitlistForm;